import {
  useNode,
  usePreviewAction,
  useRunAction,
  useTask,
  useUpdateNode,
  useUploadExportExcelTemplate,
  useWorkflow,
} from "@/api/workflow-hooks";
import { Button } from "@/components/button";
import { Checkbox } from "@/components/checkbox";
import DevinPdfViewer from "@/components/devinPdfViewer";
import { Label } from "@/components/label";
import { LoadingSpinner } from "@/components/loader";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/tabs";
import { cn } from "@/lib/utils";
import { DownloadIcon } from "@radix-ui/react-icons";
import {
  DOCUMENT_GROUP_NODE_NAME,
  RULE_GROUP_HOLDER_NODE_NAME,
} from "@repo/shared/migration-types";
import type {
  ActionNode,
  DocumentNode,
  GroupNode,
} from "@repo/shared/workflow";
import { produce } from "immer";
import { RefreshCw } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { RunnerProps } from "../shared";
import ExcelEditor from "./Excel";
import { ExcelPreview } from "./ExcelPreview";

type ContentType = "extractions" | "rules";

export const ExportEditor: React.FC<{
  workflowId: string;
  actionId: string;
}> = ({ workflowId, actionId }) => {
  const updateNodeMut = useUpdateNode(workflowId);
  const uploadTemplateMut = useUploadExportExcelTemplate(workflowId);
  const { data: _action } = useNode(workflowId, actionId);
  const { data: workflowBody } = useWorkflow(workflowId);
  const workflow = workflowBody?.workflow;

  const [wasEdited, setWasEdited] = useState(false);
  const [action, setAction] = useState<ActionNode | null>(null);
  useEffect(() => {
    if (_action && !action) setAction(_action as ActionNode);
  }, [_action, action]);

  const {
    ruleGroups,
    extractions: models,
    selectedTab,
  } = useMemo(() => {
    if (!workflow) return { ruleGroups: [], extractions: [] };

    const ruleGroupHolder = workflow.nodes.find(
      (n) => n.name === RULE_GROUP_HOLDER_NODE_NAME,
    );
    const ruleGroups = ruleGroupHolder?.dependencyIds.map((id) =>
      workflow.nodes.find((n) => n.id === id),
    ) as GroupNode[];

    const documentGroup = workflow.nodes.find(
      (n) => n.name === DOCUMENT_GROUP_NODE_NAME,
    );
    const extractions = documentGroup?.dependencyIds.map((id) =>
      workflow.nodes.find((n) => n.id === id),
    ) as DocumentNode[];

    const ruleGroupIds = ruleGroups.map((g) => g.id);
    const extractionIds = extractions.map((e) => e.id);

    const depsIncludesRules = action?.dependencyIds.some((id) =>
      ruleGroupIds.includes(id),
    );
    const depsIncludesExtractions = action?.dependencyIds.some((id) =>
      extractionIds.includes(id),
    );

    const selectedTab: ContentType | null = depsIncludesExtractions
      ? "extractions"
      : depsIncludesRules
        ? "rules"
        : null;

    return {
      ruleGroups,
      extractions,
      selectedTab,
    };
  }, [workflow, action]);

  const handleUpdate = useCallback((update: ActionNode) => {
    setAction(update);
    setWasEdited(true);
  }, []);

  const handleSave = useCallback(async () => {
    if (wasEdited && action) {
      await updateNodeMut.mutateAsync({
        nodeId: action.id,
        update: action,
      });

      setWasEdited(false);
    }
  }, [wasEdited, action, updateNodeMut]);

  if (!action) return null;
  if (action.subType !== "export") {
    return <div>Not an export action</div>;
  }

  return (
    <div className="max-w-[1080px] p-4 gap-y-8">
      <Label className="block mt-2 font-medium pb-2">Content</Label>
      <div className="grid grid-cols-2 border w-full rounded-md divide-x">
        <div
          className={cn("flex flex-col p-5", {
            "bg-gray-200 text-gray-600": selectedTab === "extractions",
          })}
        >
          <Label className="block mb-2 font-medium pb-2">Rule Groups</Label>
          <div className="space-y-4 flex flex-col">
            {ruleGroups?.map((group) => (
              <div key={group.id} className="flex items-center space-x-2">
                <Checkbox
                  id={`group-${group.id}`}
                  disabled={selectedTab === "extractions"}
                  checked={action?.dependencyIds?.includes(group.id)}
                  onCheckedChange={(checked) => {
                    const newAction = produce(action, (draft) => {
                      if (checked && !draft.dependencyIds.includes(group.id)) {
                        draft.dependencyIds.push(group.id);
                      } else if (!checked) {
                        draft.dependencyIds = draft.dependencyIds.filter(
                          (id) => id !== group.id,
                        );
                      }

                      // Hard code this configuration for rules
                      if (
                        draft.config.format === "csv" ||
                        draft.config.format === "pdf"
                      ) {
                        draft.config.fields = ["result", "reasoning"];
                        draft.config.orderBy = [
                          {
                            field: "result",
                            method: {
                              type: "value-order",
                              order: ["Failed", "Passed", "Not Applicable"],
                            },
                          },
                        ];
                      }
                    });

                    handleUpdate(newAction);
                  }}
                />
                <Label htmlFor={`group-${group.id}`}>{group.name}</Label>
              </div>
            ))}
          </div>
          {selectedTab === "extractions" && (
            <div className="text-xs italic mt-4">
              Cannot select rules if extractions are selected.
            </div>
          )}
        </div>
        <div
          className={cn("flex flex-col p-5", {
            "bg-gray-200 text-gray-600": selectedTab === "rules",
          })}
        >
          <Label className="block mb-2 font-medium pb-2">Extractions</Label>
          <div className="space-y-4 flex flex-col">
            {models?.map((model) => (
              <div key={model.id} className="flex items-center space-x-2">
                <Checkbox
                  id={`extraction-${model.id}`}
                  disabled={selectedTab === "rules"}
                  checked={action?.dependencyIds?.includes(model.id)}
                  onCheckedChange={(checked) => {
                    const newAction = produce(action, (draft) => {
                      if (checked && !draft.dependencyIds.includes(model.id)) {
                        draft.dependencyIds.push(model.id);
                      } else if (!checked) {
                        draft.dependencyIds = draft.dependencyIds.filter(
                          (id) => id !== model.id,
                        );
                      }
                    });

                    handleUpdate(newAction);
                  }}
                />
                <Label htmlFor={`extraction-${model.id}`}>{model.name}</Label>
              </div>
            ))}
          </div>
          {selectedTab === "rules" && (
            <div className="text-xs italic mt-4">
              Cannot select extractions if rules are selected.
            </div>
          )}
        </div>
      </div>
      <Label className="block mt-5 mb-2 font-medium">Run Mode</Label>
      <Tabs
        value={action.runMode}
        onValueChange={(value: string) => {
          const newAction = produce(action, (draft) => {
            draft.runMode = value as "automatic" | "manual";
          });
          handleUpdate(newAction);
        }}
      >
        <TabsList className="grid w-fit grid-cols-2">
          <TabsTrigger value="manual">Manual</TabsTrigger>
          <TabsTrigger value="automatic">Automatic</TabsTrigger>
        </TabsList>
        <TabsContent value="manual">
          <p className="text-sm text-muted-foreground">
            The export will need to be manually run by a person.
          </p>
        </TabsContent>
        <TabsContent value="automatic">
          <p className="text-sm text-muted-foreground">
            The export will run automatically when it's ready.
          </p>
        </TabsContent>
      </Tabs>
      <Label className="block mt-5 mb-2 font-medium">Export Type</Label>
      <Select
        value={action.config.format}
        onValueChange={(value) => {
          if (!action) {
            return;
          }

          const newAction = produce(action, (draft) => {
            const config =
              value === "excel"
                ? {
                    format: "excel" as const,
                    editTemplate: { sheets: [] },
                  }
                : value === "csv"
                  ? {
                      format: "csv" as const,
                      fields: [],
                    }
                  : {
                      format: "pdf" as const,
                      fields: [],
                    };

            draft.config = config;
          });

          handleUpdate(newAction);
        }}
      >
        <SelectTrigger>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="csv">CSV</SelectItem>
          <SelectItem value="pdf">PDF</SelectItem>
          <SelectItem value="excel">Excel</SelectItem>
        </SelectContent>
      </Select>
      {action.config.format === "excel" && (
        <>
          <Label className="block mt-5 mb-2 font-medium">Template</Label>
          <ExcelEditor
            workflowId={workflowId}
            actionId={actionId}
            definition={action.config}
            onConfigChange={(update) => {
              handleUpdate({
                ...action,
                config: update,
              });
            }}
            onFileUpload={async (file) => {
              const updatedAction = await uploadTemplateMut.mutateAsync({
                nodeId: actionId,
                file,
              });

              setAction(updatedAction as ActionNode);
            }}
          />
        </>
      )}
      <div />
      <div className="flex justify-end mt-4">
        <Button
          disabled={!wasEdited || updateNodeMut.isPending}
          onClick={handleSave}
        >
          Save
        </Button>
      </div>
    </div>
  );
};

function base64ToUint8Array(base64String: string): Uint8Array {
  // Decode base64 to binary string
  const binaryString = atob(base64String);

  // Create Uint8Array from binary string
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }

  return bytes;
}

const createCSV = (body: string) => {
  const blob = new Blob([base64ToUint8Array(body)], { type: "text/csv" });
  return URL.createObjectURL(blob);
};

const createPDF = (body: string) => {
  const blob = new Blob([base64ToUint8Array(body)], {
    type: "application/pdf",
  });
  return URL.createObjectURL(blob);
};

const createExcel = (body: string) => {
  const blob = new Blob([base64ToUint8Array(body)], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  return URL.createObjectURL(blob);
};

export const Console: React.FC<RunnerProps> = ({
  taskId,
  runId,
  actionNode: node,
}) => {
  const previewMut = usePreviewAction(taskId);
  const [previewResult, setPreviewResult] = useState<{
    base64: string;
    fileType: string;
  } | null>(null);
  const [csvResult, setCsvResult] = useState<string | null>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: no need to depend on previewResult
  const handlePreview = useCallback(async () => {
    const result = await previewMut.mutateAsync({
      nodeId: node.id,
      runId,
    });

    setPreviewResult(result.data);

    if (result.data?.fileType === "csv") {
      const csv = await new Blob([base64ToUint8Array(result.data.base64)], {
        type: "text/csv",
      }).text();

      setCsvResult(csv);
    }
  }, [node.id, runId]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: no need to depend on previewMut
  useEffect(() => {
    const fn = async () => {
      if (!previewResult && !previewMut.isPending) {
        await handlePreview();
      }
    };

    fn();
  }, [previewResult, handlePreview]);

  const pdfUrl = useMemo(() => {
    const pdfBody = previewResult?.fileType.includes("pdf")
      ? base64ToUint8Array(previewResult.base64)
      : null;
    if (!pdfBody) return null;

    const blob = new Blob([pdfBody], { type: "application/pdf" });
    return URL.createObjectURL(blob);
  }, [previewResult]);

  return (
    <div className="my-4">
      <div className="flex items-center justify-between mb-4">
        <h1 className="font-bold text-lg">{node.name}</h1>
        <Button
          variant="outline"
          size="icon"
          onClick={handlePreview}
          disabled={previewMut.isPending}
        >
          <RefreshCw
            className={cn("w-4 h-4", {
              "animate-spin": previewMut.isPending,
            })}
          />
        </Button>
      </div>
      {previewResult?.fileType.includes("csv") ? (
        <pre className="border overflow-auto bg-secondary p-4 rounded-md max-h-[calc(100dvh-16rem)]">
          {csvResult}
        </pre>
      ) : previewResult?.fileType.includes("xlsx") ? (
        <ExcelPreview bytes={base64ToUint8Array(previewResult.base64 ?? "")} />
      ) : (
        <div className="w-full h-[calc(100dvh-10rem)]">
          <DevinPdfViewer fileUrl={pdfUrl ?? ""} />
        </div>
      )}
    </div>
  );
};

export const RHS: React.FC<RunnerProps> = ({
  taskId,
  runId,
  actionNode: action,
}) => {
  const runMut = useRunAction(taskId);
  const { data: task } = useTask(taskId);
  const [result, setResult] = useState<
    "not_run" | "success" | "running" | "error" | "system_error"
  >("not_run");

  const handleDownload = async () => {
    setResult("running");
    try {
      const taskName = task?.name;
      const result = await runMut.mutateAsync({
        nodeId: action.id,
        runId,
      });

      if (result.data) {
        const body = result.data.base64 as string;
        const fileType = result.data.fileType as string;
        const fileName = `${taskName}-${action.name}.${fileType}`;
        const url =
          fileType === "pdf"
            ? createPDF(body)
            : fileType === "xlsx"
              ? createExcel(body)
              : createCSV(body);
        const a = document.createElement("a");
        a.href = url;
        a.download = fileName;
        a.click();
        URL.revokeObjectURL(url);
        setResult("success");

        // TODO: show history
      } else {
        setResult("error");
      }
    } catch (e) {
      setResult("system_error");
    }
  };

  return (
    <div className="mt-8">
      <Button onClick={handleDownload} disabled={runMut.isPending}>
        {runMut.isPending ? (
          <div className="mr-2 mt-0.5">
            <LoadingSpinner size="small" />
          </div>
        ) : (
          <DownloadIcon className="w-4 h-4 mr-2" />
        )}
        Download
      </Button>
      {result !== "not_run" && (
        <div
          className={cn("mt-2 p-4 border rounded-md", {
            "bg-green-100 border-green-300 text-green-700":
              result === "success",
            "bg-orange-100 border-orange-300 text-orange-700":
              result === "error",
            "bg-yellow-100 border-yellow-300 text-yellow-700":
              result === "running",
            "bg-red-100 border-red-300 text-red-700": result === "system_error",
          })}
        >
          {result === "success" && <p>Export downloaded successfully.</p>}
          {result === "error" && <p>Export failed.</p>}
          {result === "system_error" && <p>Something went wrong.</p>}
          {result === "running" && <p>Generating export...</p>}
        </div>
      )}
    </div>
  );
};
